import Component from '@glimmer/component';

// Components
import {
  HdsTable,
  HdsIcon,
} from '@hashicorp/design-system-components/components';

// Helpers
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import { eq } from 'ember-truth-helpers';

import type { HashicorpCloudResourcemanagerRole } from '@clients/cloud-resource-manager';
import type { CategoryAction } from '../../../types/role-definitions-flyout';

export interface RoleComparisonTableSignature {
  Args: {
    roles: Array<HashicorpCloudResourcemanagerRole>;
    actions: Array<CategoryAction>;
    tableType: string;
  };
  Element: HTMLDivElement;
}

export default class RoleComparisonTableComponent extends Component<RoleComparisonTableSignature> {
  <template>
    <HdsTable data-test-role-comparison-table>
      <:head as |H|>
        <H.Tr>
          <H.Th @width="226" data-test-role-comparison-table-ability-header>{{t
              "manage-access.components.table.role-comparison.header.ability"
              ability=@tableType
            }}</H.Th>
          {{#each @roles as |column|}}
            <H.Th
              data-test-role-comparison-table-role-header
            >{{column.title}}</H.Th>
          {{/each}}
        </H.Tr>
      </:head>
      <:body as |B|>
        {{#each @actions as |row index|}}
          <B.Tr>
            <B.Td
              width={{if (eq index 0) "226" "auto"}}
              data-test-role-comparison-table-action-header
            >
              {{row.title}}
            </B.Td>
            {{#each @roles as |column|}}
              <B.Td
                @align="left"
                data-test-role-comparison-table-cell={{concat
                  column.title
                  "-"
                  row.title
                }}
              >
                {{#let (iamPermissionIsIncluded row column) as |hasPermission|}}
                  <HdsIcon
                    @color={{if hasPermission "green" "red"}}
                    @name={{if hasPermission "check" "skip"}}
                    @isInline={{true}}
                    aria-label={{t
                      "manage-access.components.table.role-comparison.aria-label"
                      hasPermission=hasPermission
                    }}
                  />
                {{/let}}
              </B.Td>
            {{/each}}
          </B.Tr>
        {{/each}}
      </:body>
    </HdsTable>
  </template>
}

const ALWAYS_ALLOWED_PERMISSIONS = ['iam.users.list', 'iam.groups.list'];

function iamPermissionIsIncluded(
  action: CategoryAction,
  column: HashicorpCloudResourcemanagerRole,
): boolean {
  const { overrideColumnPermissionCheck, permissions: requiredPermissions } =
    action;
  const { permissions: rolePermissions = [] } = column;

  if (
    overrideColumnPermissionCheck &&
    typeof overrideColumnPermissionCheck === 'function'
  ) {
    return overrideColumnPermissionCheck(column);
  }

  return requiredPermissions.every(
    (permission) =>
      rolePermissions.includes(permission) ||
      ALWAYS_ALLOWED_PERMISSIONS.includes(permission),
  );
}
